import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export interface CompanyRow {
  id: string;
  name: string;
  entity_type: string;
  parent_company_id: string | null;
  industry: string | null;
  logo_url: string | null;
}

export interface EntityTree {
  standalone: CompanyRow[];
  organizations: {
    org: CompanyRow;
    locations: CompanyRow[];
  }[];
}

interface EntityContextValue {
  entityTree: EntityTree | null;
  isLoading: boolean;
  activeCompanyId: string | null;
  activeOrgId: string | null;
  isConsolidatedView: boolean;
  switchToCompany: (companyId: string) => void;
  switchToOrg: (orgId: string) => void;
  hasMultipleEntities: boolean;
  refetchTree: () => Promise<void>;
}

const EntityContext = createContext<EntityContextValue | null>(null);

const STORAGE_KEY = 'pulse_active_entity';

function getStoredEntityId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredEntityId(id: string, isOrg: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
    localStorage.setItem(`${STORAGE_KEY}_is_org`, isOrg ? '1' : '0');
  } catch {
    // ignore
  }
}

function getStoredIsOrg(): boolean {
  try {
    return localStorage.getItem(`${STORAGE_KEY}_is_org`) === '1';
  } catch {
    return false;
  }
}

function firstCompanyId(tree: EntityTree): string | null {
  if (tree.standalone.length > 0) return tree.standalone[0].id;
  if (tree.organizations.length > 0) {
    const { org, locations } = tree.organizations[0];
    return locations.length > 0 ? locations[0].id : org.id;
  }
  return null;
}

function isValidId(id: string, tree: EntityTree): boolean {
  const allIds = new Set<string>();
  for (const c of tree.standalone) allIds.add(c.id);
  for (const { org, locations } of tree.organizations) {
    allIds.add(org.id);
    for (const loc of locations) allIds.add(loc.id);
  }
  return allIds.has(id);
}

export function EntityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [entityTree, setEntityTree] = useState<EntityTree | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [isConsolidatedView, setIsConsolidatedView] = useState(false);

  const fetchTree = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-entity-tree`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (!response.ok) {
        setIsLoading(false);
        return;
      }

      const result = await response.json();
      const tree: EntityTree = result.data;
      setEntityTree(tree);

      // Resolve active entity
      const storedId = getStoredEntityId();
      const storedIsOrg = getStoredIsOrg();

      if (storedId && isValidId(storedId, tree)) {
        if (storedIsOrg) {
          // Stored as consolidated org view
          setActiveOrgId(storedId);
          setIsConsolidatedView(true);
          setActiveCompanyId(storedId);
        } else {
          setActiveCompanyId(storedId);
          setActiveOrgId(null);
          setIsConsolidatedView(false);
        }
      } else {
        const defaultId = firstCompanyId(tree);
        setActiveCompanyId(defaultId);
        setActiveOrgId(null);
        setIsConsolidatedView(false);
        if (defaultId) setStoredEntityId(defaultId, false);
      }
    } catch (err) {
      console.error('[EntityContext] Error fetching entity tree:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setEntityTree(null);
      setActiveCompanyId(null);
      setActiveOrgId(null);
      setIsConsolidatedView(false);
      setIsLoading(false);
    }
  }, [user]);

  const switchToCompany = useCallback((companyId: string) => {
    setActiveCompanyId(companyId);
    setActiveOrgId(null);
    setIsConsolidatedView(false);
    setStoredEntityId(companyId, false);
  }, []);

  const switchToOrg = useCallback((orgId: string) => {
    setActiveOrgId(orgId);
    setActiveCompanyId(orgId);
    setIsConsolidatedView(true);
    setStoredEntityId(orgId, true);
  }, []);

  const totalEntityCount = entityTree
    ? entityTree.standalone.length +
      entityTree.organizations.reduce((sum, { locations }) => sum + locations.length + 1, 0)
    : 0;

  const hasMultipleEntities = totalEntityCount > 1;

  return (
    <EntityContext.Provider value={{
      entityTree,
      isLoading,
      activeCompanyId,
      activeOrgId,
      isConsolidatedView,
      switchToCompany,
      switchToOrg,
      hasMultipleEntities,
      refetchTree: fetchTree,
    }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useEntity(): EntityContextValue {
  const ctx = useContext(EntityContext);
  if (!ctx) throw new Error('useEntity must be used inside EntityProvider');
  return ctx;
}
